import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { apiService, getApiErrorMessage } from '../services/apiService';
import {
  PROJEKTTERVEZES_FELADAT_COLOR_PRESETS,
  PROJEKTTERVEZES_FELADAT_STATUS_LABELS,
  PROJEKTTERVEZES_FELADAT_STATUS_OPTIONS,
  ProjekttervezesFeladat,
  ProjekttervezesFeladatStatus,
} from '../services/projekttervezesService';
import { parseApiDate, toApiDate } from '../utils/workDays';

interface UserOption {
  id: string;
  userName: string;
  displayName: string;
}

export interface FeladatDialogValue {
  nev: string;
  leiras: string;
  kezdetiDatum: Date | null;
  befejezesDatum: Date | null;
  status: ProjekttervezesFeladatStatus;
  szin: string;
  selectedUsers: UserOption[];
}

interface AddProjekttervezesFeladatDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initialFeladat?: ProjekttervezesFeladat | null;
  onClose: () => void;
  onSave: (value: FeladatDialogValue) => Promise<void>;
}

const extractArrayData = (response: any): any[] => {
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response?.data)) {
    return response.data;
  }
  return [];
};

const emptyValue = (): FeladatDialogValue => ({
  nev: '',
  leiras: '',
  kezdetiDatum: null,
  befejezesDatum: null,
  status: 'inProgress',
  szin: PROJEKTTERVEZES_FELADAT_COLOR_PRESETS[0],
  selectedUsers: [],
});

const AddProjekttervezesFeladatDialog: React.FC<AddProjekttervezesFeladatDialogProps> = ({
  open,
  mode,
  initialFeladat,
  onClose,
  onSave,
}) => {
  const [value, setValue] = useState<FeladatDialogValue>(emptyValue);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setError('');
    setSaving(false);

    const loadUsers = async () => {
      try {
        const usersResponse = await apiService.get('/users?activeOnly=true');
        const mapped = extractArrayData(usersResponse).map((item: any) => ({
          id: String(item.id || item.userId || item.userID || item.userid || item.userName),
          userName: String(item.userName || item.UserName || ''),
          displayName: String(item.displayName || item.DisplayName || item.userName || item.UserName || ''),
        }));

        const uniqueById = new Map<string, UserOption>();
        mapped.forEach((user: UserOption) => {
          if (!user.id) {
            return;
          }
          uniqueById.set(user.id, {
            id: user.id,
            userName: user.userName,
            displayName: user.displayName || user.userName,
          });
        });

        const loadedUsers = Array.from(uniqueById.values());
        setUsers(loadedUsers);

        if (mode === 'edit' && initialFeladat) {
          setValue({
            nev: initialFeladat.nev,
            leiras: initialFeladat.leiras,
            kezdetiDatum: parseApiDate(initialFeladat.kezdetiDatum),
            befejezesDatum: parseApiDate(initialFeladat.befejezesDatum),
            status: initialFeladat.status,
            szin: initialFeladat.szin || PROJEKTTERVEZES_FELADAT_COLOR_PRESETS[0],
            selectedUsers: initialFeladat.felelosok
              .map((felelos) => loadedUsers.find((user) => user.id === felelos.userId))
              .filter((user): user is UserOption => !!user),
          });
        } else {
          setValue(emptyValue());
        }
      } catch (error) {
        setUsers([]);
        setError(getApiErrorMessage(error, 'Nem sikerült betölteni a felhasználókat.'));
        setValue(mode === 'edit' && initialFeladat
          ? {
              nev: initialFeladat.nev,
              leiras: initialFeladat.leiras,
              kezdetiDatum: parseApiDate(initialFeladat.kezdetiDatum),
              befejezesDatum: parseApiDate(initialFeladat.befejezesDatum),
              status: initialFeladat.status,
              szin: initialFeladat.szin || PROJEKTTERVEZES_FELADAT_COLOR_PRESETS[0],
              selectedUsers: [],
            }
          : emptyValue());
      }
    };

    loadUsers();
  }, [open, mode, initialFeladat]);

  const handleSave = async () => {
    if (!value.nev.trim()) {
      setError('A feladat neve kötelező.');
      return;
    }

    if (value.kezdetiDatum && value.befejezesDatum && value.kezdetiDatum > value.befejezesDatum) {
      setError('A kezdeti dátum nem lehet későbbi, mint a befejezés dátuma.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onSave(value);
      onClose();
    } catch (saveError: any) {
      setError(getApiErrorMessage(saveError, 'Nem sikerült menteni a feladatot.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{mode === 'create' ? 'Feladat hozzáadása' : 'Feladat szerkesztése'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Feladat neve"
            value={value.nev}
            onChange={(event) => setValue((prev) => ({ ...prev, nev: event.target.value }))}
            fullWidth
            required
          />

          <TextField
            label="Leírás"
            value={value.leiras}
            onChange={(event) => setValue((prev) => ({ ...prev, leiras: event.target.value }))}
            fullWidth
            multiline
            minRows={3}
          />

          <DatePicker
            label="Kezdeti dátum"
            value={value.kezdetiDatum}
            onChange={(newValue) => setValue((prev) => ({ ...prev, kezdetiDatum: newValue }))}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <DatePicker
            label="Befejezés / határidő"
            value={value.befejezesDatum}
            onChange={(newValue) => setValue((prev) => ({ ...prev, befejezesDatum: newValue }))}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <Autocomplete
            multiple
            options={users}
            value={value.selectedUsers}
            onChange={(_, selectedUsers) => setValue((prev) => ({ ...prev, selectedUsers }))}
            getOptionLabel={(option) => option.displayName}
            isOptionEqualToValue={(option, selected) => option.id === selected.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Felelősök"
                placeholder="Kezdjen el gépelni a kereséshez..."
              />
            )}
            noOptionsText="Nincs találat"
          />

          <TextField
            select
            fullWidth
            label="Állapot"
            value={value.status}
            onChange={(event) =>
              setValue((prev) => ({ ...prev, status: event.target.value as ProjekttervezesFeladatStatus }))
            }
          >
            {PROJEKTTERVEZES_FELADAT_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {PROJEKTTERVEZES_FELADAT_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Gantt szín
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PROJEKTTERVEZES_FELADAT_COLOR_PRESETS.map((color) => (
                <Box
                  key={color}
                  component="button"
                  type="button"
                  onClick={() => setValue((prev) => ({ ...prev, szin: color }))}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    border: value.szin === color ? '2px solid #1976d2' : '1px solid #d0d7de',
                    backgroundColor: color,
                    cursor: 'pointer',
                    p: 0,
                  }}
                  aria-label={color}
                />
              ))}
            </Stack>
            <TextField
              size="small"
              label="Hex szín"
              value={value.szin}
              onChange={(event) => setValue((prev) => ({ ...prev, szin: event.target.value }))}
              sx={{ mt: 1.5, width: 140 }}
            />
          </Box>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Mégse</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {mode === 'create' ? 'Hozzáadás' : 'Mentés'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddProjekttervezesFeladatDialog;

export const feladatDialogValueToPayload = (value: FeladatDialogValue) => ({
  nev: value.nev.trim(),
  leiras: value.leiras.trim(),
  kezdetiDatum: toApiDate(value.kezdetiDatum),
  befejezesDatum: toApiDate(value.befejezesDatum),
  status: value.status,
  szin: value.szin,
  userIds: value.selectedUsers.map((user) => user.id),
});
